import { useMemo } from 'react';

const GRID = 25;

/**
 * A QR-shaped pattern: the three finder squares a scanner looks for, and a
 * seeded scatter of modules between them.
 *
 * It is not a real code and does not scan - a mock should never send a
 * visitor's camera to a made-up shop - but the seed is the mode, so each
 * screen gets its own stable pattern instead of one picture repeated.
 */
function qrModules(seed: string): boolean[] {
  let state = 0;
  for (const char of seed) state = (state * 31 + char.charCodeAt(0)) >>> 0;
  const next = () => {
    state = (state * 1664525 + 1013904223) >>> 0;
    return state / 2 ** 32;
  };

  const inFinder = (x: number, y: number) =>
    (x < 8 && y < 8) || (x >= GRID - 8 && y < 8) || (x < 8 && y >= GRID - 8);

  const cells: boolean[] = [];
  for (let y = 0; y < GRID; y += 1) {
    for (let x = 0; x < GRID; x += 1) {
      cells.push(inFinder(x, y) ? false : next() > 0.52);
    }
  }
  return cells;
}

/** The pattern drawn as crisp squares; it fills whatever box it is put in. */
export function FakeQr({ seed }: { seed: string }) {
  const cells = useMemo(() => qrModules(seed), [seed]);
  const finders = [
    [0, 0],
    [GRID - 7, 0],
    [0, GRID - 7],
  ];

  return (
    <svg viewBox={`-2 -2 ${GRID + 4} ${GRID + 4}`} className="block size-full" shapeRendering="crispEdges">
      <rect x="-2" y="-2" width={GRID + 4} height={GRID + 4} fill="#ffffff" />
      {cells.map((on, index) =>
        on ? (
          <rect key={index} x={index % GRID} y={Math.floor(index / GRID)} width="1" height="1" fill="#0b1220" />
        ) : null,
      )}
      {finders.map(([x, y]) => (
        <g key={`${x}-${y}`}>
          <rect x={x} y={y} width="7" height="7" fill="#0b1220" />
          <rect x={x + 1} y={y + 1} width="5" height="5" fill="#ffffff" />
          <rect x={x + 2} y={y + 2} width="3" height="3" fill="#0b1220" />
        </g>
      ))}
    </svg>
  );
}
